import React, { useState, useMemo, useEffect, useCallback } from 'react'
import moment from 'moment'
import axios from 'axios'
import { config } from '../assets/config/config'
import ThemedSuspense from '../components/ThemedSuspense'
import { identifyBrevoUser, resetBrevoChat } from '../utils/brevo'
import { trackUserProfile } from '../utils/posthog'
import { setMetaUser, trackMetaRegistration } from '../utils/metaPixel'
import { trackGoogleSignup, setGoogleAdsUser, confirmPendingPurchases } from '../utils/googleAds'
import { stripeCheckoutService } from '../services/stripe-checkout.service'

const apiUrl = config.api.url

// create context
export const AuthContext = React.createContext()

export const AuthProvider = ({ children }) => {
  const [isLoaded, setLoaded] = useState(false)
  const [user, setUser] = useState(null)
  const [isImpersonating, setImpersonating] = useState(false)
  const [accessToken, setAccessToken] = useState(null)
  const [timeoutRef, setTimeoutRef] = useState(null)

  const refreshTokens = useCallback(
    () => {
      return axios.post(`${apiUrl}/v1/auth/refresh-tokens`, {})
      .then(response => {
        const user = response.data.user;
        setAccessToken(response.data.token)
        setImpersonating(false)
        setUser(user)
        
        // Re-identify user on token refresh (returning users)
        if (user && user.email) {
          trackUserProfile(user.email, {
            name: user.name,
            email: user.email,
            user_id: user.id,
            is_email_verified: user.isEmailVerified,
            plan_type: user.plan || 'free',
            session_type: 'returning',
            last_active: new Date().toISOString()
          });
        }
        
        return response
      })
      .catch(error => {
        setUser(null)
        setAccessToken(null)
        return error
      })
    },
    []
  )

  const refreshUser = useCallback(
    () => {
      if (!user) return;
      
      return axios.get(`${apiUrl}/v1/users/${user.id}`)
      .then(response => {
        if (response.status === 200 && response.data) {
          identifyBrevoUser(response.data.email)
          if (JSON.stringify(user) != JSON.stringify(response.data)) {
            setUser(response.data)
          }
        }
      })
      // Background poll: a sleeping laptop or dropped wifi fails it, and the
      // next tick retries. Unhandled, each miss was logged as "Network Error".
      .catch(() => {})
    },
    [user]
  )

  useEffect(() => {
    // Keep credits/subscription fresh without hammering the API. Every user
    // fetch populates the full task history server-side, so a tight interval
    // here translates directly into constant server load.
    const interval = setInterval(() => {
      refreshUser();
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshUser]);

  const syncLogout = (event) => {
    if (event.key === 'logout') {
      setAccessToken(null)
      setUser(null)
    }
  }

  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(
      config => {
        config.withCredentials = true
        config.credentials = 'include'
        // _retried requests already carry the freshly refreshed token
        if(accessToken && !config._retried) {
          config.headers.Authorization = `Bearer ${accessToken.token}`
        }
        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    return () => {
      axios.interceptors.request.eject(interceptorId);
    }
  }, [accessToken])

  // The scheduled refresh below is a setTimeout, which browsers throttle in
  // background tabs / sleep, so the 30-min access token can lapse. On a 401,
  // refresh once (shared across concurrent requests) and retry.
  useEffect(() => {
    let inflight = null
    const interceptorId = axios.interceptors.response.use(
      response => response,
      error => {
        const original = error.config
        if (
          error.response?.status !== 401 ||
          !original ||
          original._retried ||
          /\/auth\/(refresh-tokens|login|logout|register)/.test(original.url || '')
        ) {
          return Promise.reject(error)
        }
        original._retried = true
        inflight = inflight || refreshTokens().finally(() => { inflight = null })
        return inflight.then(response => {
          const token = response?.data?.token?.token
          if (!token) return Promise.reject(error)
          original.headers.Authorization = `Bearer ${token}`
          return axios(original)
        })
      }
    )

    return () => {
      axios.interceptors.response.eject(interceptorId);
    }
  }, [refreshTokens])

  useEffect(() => {
    refreshTokens()
    .then(response => {
      setLoaded(true)

      // Retry Google Ads purchases that couldn't be confirmed on an earlier visit
      if (response?.data?.user) confirmPendingPurchases(stripeCheckoutService.getCheckoutSession);

      // OAuth redirect flow (Google/GitHub): new=1 only when the account was just created
      const params = new URLSearchParams(window.location.search);
      if (params.get('source') === 'oauth') {
        if (params.get('new') === '1') {
          trackMetaRegistration(response?.data?.user);
          trackGoogleSignup(response?.data?.user);
        }
        window.history.replaceState({}, '', window.location.pathname);
      }
    })
  }, [refreshTokens])

  useEffect(() => {
    if(accessToken) {
      const tokenExpires = moment(accessToken.expires)
      const tokenMaxAge = tokenExpires.diff(moment().add(1, 'minutes'))
      const ref = setTimeout(() => {
        refreshTokens()
      }, tokenMaxAge)
      return () => clearTimeout(ref)
    }
  }, [accessToken, refreshTokens])

  // Meta advanced matching for every logged-in page view, not just sign-ups
  useEffect(() => { setMetaUser(user); setGoogleAdsUser(user) }, [user])

  useEffect(() => {
    window.addEventListener('storage', syncLogout) 
    return function cleanup() {
      window.removeEventListener('storage', syncLogout)
    }
  }, [])

  const value = useMemo(
    () => {
      const register = (username, email, password) => {
        return axios.post(`${apiUrl}/v1/auth/register`, {
          name: username,
          email: email,
          password: password
        })
        .then(response => {
          const user = response.data.user;
          setAccessToken(response.data.token)
          setUser(user)
          
          // Identify user in PostHog for analytics tracking
          trackUserProfile(user.email, {
            name: user.name,
            email: user.email,
            user_id: user.id,
            signup_method: 'email',
            created_at: user.createdAt,
            is_email_verified: user.isEmailVerified,
            plan_type: user.plan || 'free',
            signup_date: new Date().toISOString()
          });

          trackMetaRegistration(user);
          trackGoogleSignup(user);
        })
      }

      const login = (email, password) => {
        return axios.post(`${apiUrl}/v1/auth/login`, {
          email: email,
          password: password
        })
        .then(response => {
          const user = response.data.user;
          setAccessToken(response.data.token)
          setUser(user)
          
          // Identify user in PostHog for analytics tracking
          trackUserProfile(user.email, {
            name: user.name,
            email: user.email,
            user_id: user.id,
            login_method: 'email',
            is_email_verified: user.isEmailVerified,
            plan_type: user.plan || 'free',
            last_login: new Date().toISOString()
          });
        })
      }

      const githubAuth = () => {
        window.location.href = `${apiUrl}/v1/auth/github`;
      }

      const googleAuth = () => {
        window.location.href = `${apiUrl}/v1/auth/google`;
      }

      const logout = () => {
        setAccessToken(null)
        setUser(null)
        resetBrevoChat()
        return axios.post(`${apiUrl}/v1/auth/logout`, {})
        .then(response => {
          window.localStorage.setItem('logout', moment())
        })
        .catch(err => {})
      }

      const forgotPassword = (email) => {
        return axios.post(`${apiUrl}/v1/auth/forgot-password`, {
          email: email
        })
      }

      const resetPassword = (password, resetToken) => {
        return axios.post(`${apiUrl}/v1/auth/reset-password?token=${resetToken}`, {
          password: password
        })
      }

      const resendVerificationEmail = (userId) => {
        return axios.post(`${apiUrl}/v1/auth/resend-verification-email`, {
          userId: userId,
        })
      }

      const verifyEmail = (emailVerificationToken) => {
        return axios.post(`${apiUrl}/v1/auth/verify-email?token=${emailVerificationToken}`, {})
        .then(response => {
          // The server signs the user in on a successful verification.
          if (response.data.token && response.data.user) {
            setAccessToken(response.data.token)
            setUser(response.data.user)
          }
          return response
        })
      }

      const impersonateUser = (userId) => {
        if (isImpersonating) return
        return axios.post(`${apiUrl}/v1/auth/impersonate-user/${userId}`, {})
        .then(response => {
          setImpersonating(true)
          setAccessToken(response.data.token)
          setUser(response.data.user)
        })
      }

      const stopImpersonation = () => {
        if (!isImpersonating) return;
        refreshTokens()
        .then(() => {
          setImpersonating(false)
        })
      }

      const googleOneTapAuth = (credential) => {
        console.log('Google One Tap auth called with credential length:', credential ? credential.length : 0);
        return axios.post(`${apiUrl}/v1/auth/google-one-tap`, {
          credential: credential
        })
        .then(response => {
          console.log('Google One Tap auth success:', { userId: response.data.user.id });
          const user = response.data.user;
          setAccessToken(response.data.token)
          setUser(user)
          
          // Identify user in PostHog for analytics tracking
          trackUserProfile(user.email, {
            name: user.name,
            email: user.email,
            user_id: user.id,
            signup_method: 'google_one_tap',
            login_method: 'google_one_tap',
            is_email_verified: user.isEmailVerified,
            plan_type: user.plan || 'free',
            last_login: new Date().toISOString()
          });

          if (response.data.isNewUser) {
            trackMetaRegistration(user);
            trackGoogleSignup(user);
          }
        })
        .catch(error => {
          console.error('Google One Tap auth error:', error.response?.data || error.message);
          throw error;
        })
      }

      return ({
        user,
        setUser,
        refreshUser,
        register,
        accessToken,
        login,
        githubAuth,
        googleAuth,
        logout,
        forgotPassword,
        resetPassword,
        resendVerificationEmail,
        verifyEmail,
        isImpersonating,
        impersonateUser,
        stopImpersonation,
        googleOneTapAuth,
      })
    },
    [user, isImpersonating, refreshUser]
  )

  if(!isLoaded) {
      return <ThemedSuspense />
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
